import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";
import { authenticate } from "../firebase-config";
import { useSnackbar } from "notistack";
import { Box, Typography } from "@mui/material";
import { Star } from "lucide-react";
import { getBackendBaseURL } from "../axios";
import Button from "../components/ui/Button";
import Logo from "../ImageAssets/convertdm-logo-full.png";

const COUNTRY_CODES = [
  { code: "+91", flag: "🇮🇳", country: "India" },
  { code: "+1", flag: "🇺🇸", country: "USA" },
  { code: "+44", flag: "🇬🇧", country: "UK" },
  { code: "+61", flag: "🇦🇺", country: "Australia" },
  { code: "+971", flag: "🇦🇪", country: "UAE" },
  { code: "+65", flag: "🇸🇬", country: "Singapore" },
  { code: "+49", flag: "🇩🇪", country: "Germany" },
  { code: "+33", flag: "🇫🇷", country: "France" },
  { code: "+81", flag: "🇯🇵", country: "Japan" },
  { code: "+86", flag: "🇨🇳", country: "China" },
  { code: "+55", flag: "🇧🇷", country: "Brazil" },
  { code: "+7", flag: "🇷🇺", country: "Russia" },
  { code: "+82", flag: "🇰🇷", country: "South Korea" },
  { code: "+39", flag: "🇮🇹", country: "Italy" },
  { code: "+34", flag: "🇪🇸", country: "Spain" },
  { code: "+60", flag: "🇲🇾", country: "Malaysia" },
  { code: "+62", flag: "🇮🇩", country: "Indonesia" },
  { code: "+63", flag: "🇵🇭", country: "Philippines" },
  { code: "+66", flag: "🇹🇭", country: "Thailand" },
  { code: "+27", flag: "🇿🇦", country: "South Africa" },
  { code: "+966", flag: "🇸🇦", country: "Saudi Arabia" },
  { code: "+974", flag: "🇶🇦", country: "Qatar" },
  { code: "+968", flag: "🇴🇲", country: "Oman" },
  { code: "+973", flag: "🇧🇭", country: "Bahrain" },
  { code: "+977", flag: "🇳🇵", country: "Nepal" },
  { code: "+94", flag: "🇱🇰", country: "Sri Lanka" },
  { code: "+880", flag: "🇧🇩", country: "Bangladesh" },
  { code: "+92", flag: "🇵🇰", country: "Pakistan" },
  { code: "+234", flag: "🇳🇬", country: "Nigeria" },
  { code: "+254", flag: "🇰🇪", country: "Kenya" },
];

function LoginPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [step, setStep] = useState("phone"); // "phone" | "otp"
  const [sendingOTP, setSendingOTP] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);

  const recaptchaVerifierRef = useRef(null);
  const otpInputsRef = useRef([]);

  useEffect(() => {
    setupRecaptcha();
    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          // ignore cleanup errors
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const setupRecaptcha = () => {
    try {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
      }
      recaptchaVerifierRef.current = new RecaptchaVerifier(
        authenticate,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {},
          "expired-callback": () => {
            enqueueSnackbar("reCAPTCHA expired. Please try again.", {
              variant: "error",
            });
          },
        }
      );
    } catch (error) {
      console.error("Error setting up recaptcha:", error);
    }
  };

  const getFirebaseErrorMessage = (errorCode) => {
    switch (errorCode) {
      case "auth/invalid-phone-number":
        return "Invalid phone number. Please check and try again.";
      case "auth/too-many-requests":
        return "Too many attempts. Please try again later.";
      case "auth/invalid-verification-code":
        return "Incorrect OTP. Please check and try again.";
      case "auth/code-expired":
        return "OTP has expired. Please request a new one.";
      case "auth/missing-phone-number":
        return "Please enter your phone number.";
      case "auth/quota-exceeded":
        return "SMS quota exceeded. Please try again later.";
      case "auth/user-disabled":
        return "This account has been disabled. Please contact support.";
      case "auth/operation-not-allowed":
        return "Phone authentication is not enabled. Please contact support.";
      default:
        return "Something went wrong. Please try again.";
    }
  };

  const completeBackendLogin = async (idToken) => {
    const response = await axios.post(
      `${getBackendBaseURL()}auth/login`,
      { source: "instaDm" },
      {
        headers: { Authorization: idToken },
        withCredentials: true,
      }
    );
    if (response.data?.success) {
      window.location.href = "/";
    } else {
      enqueueSnackbar("Authentication failed. Please try again.", {
        variant: "error",
      });
    }
  };

  const handleSendOTP = async () => {
    const cleaned = phoneNumber.replace(/\s/g, "");
    if (!cleaned || cleaned.length < 7) {
      enqueueSnackbar("Please enter a valid phone number.", {
        variant: "warning",
      });
      return;
    }

    setSendingOTP(true);
    const fullNumber = `${countryCode}${cleaned}`;

    try {
      if (!recaptchaVerifierRef.current) {
        setupRecaptcha();
      }
      const result = await signInWithPhoneNumber(
        authenticate,
        fullNumber,
        recaptchaVerifierRef.current
      );
      setConfirmationResult(result);
      setStep("otp");
      setResendTimer(30);
      enqueueSnackbar("OTP sent successfully!", { variant: "success" });
      setTimeout(() => {
        if (otpInputsRef.current[0]) {
          otpInputsRef.current[0].focus();
        }
      }, 100);
    } catch (error) {
      console.error("Error sending OTP:", error);
      const message = getFirebaseErrorMessage(error.code);
      enqueueSnackbar(message, { variant: "error" });
      setupRecaptcha();
    } finally {
      setSendingOTP(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      otpInputsRef.current[index + 1]?.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputsRef.current[index - 1]?.focus();
    }
    if (e.key === "Enter") {
      const otpString = otp.join("");
      if (otpString.length === 6) handleVerifyOTP();
    }
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setOtp(digits);
      otpInputsRef.current[5]?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join("");
    if (otpString.length !== 6) {
      enqueueSnackbar("Please enter the complete 6-digit OTP.", {
        variant: "warning",
      });
      return;
    }
    if (!confirmationResult) {
      enqueueSnackbar("Session expired. Please request a new OTP.", {
        variant: "error",
      });
      setStep("phone");
      return;
    }

    setVerifyingOTP(true);
    try {
      const credential = await confirmationResult.confirm(otpString);
      const user = credential.user;
      const idToken = await user.getIdToken();
      await completeBackendLogin(idToken);
    } catch (error) {
      console.error("Error verifying OTP:", error);
      if (error.code) {
        enqueueSnackbar(getFirebaseErrorMessage(error.code), {
          variant: "error",
        });
      } else {
        enqueueSnackbar(
          error?.response?.data?.message ||
            "Authentication failed. Please try again.",
          { variant: "error" }
        );
      }
    } finally {
      setVerifyingOTP(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    setOtp(["", "", "", "", "", ""]);
    setupRecaptcha();
    await handleSendOTP();
  };

  const handleChangeNumber = () => {
    setStep("phone");
    setOtp(["", "", "", "", "", ""]);
    setConfirmationResult(null);
  };

  const otpReady = otp.join("").length === 6;
  const phoneReady = phoneNumber.trim().length >= 7;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        fontFamily:
          'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: "#ffffff",
      }}
    >
      {/* LEFT PANEL — form */}
      <Box
        sx={{
          flex: { xs: "1 1 auto", md: "0 0 50%" },
          display: "flex",
          flexDirection: "column",
          position: "relative",
          px: { xs: "24px", sm: "48px" },
          py: { xs: "32px", md: "40px" },
          minHeight: { xs: "100vh", md: "auto" },
        }}
      >
        {/* Logo top-left */}
        <Box sx={{ mb: { xs: "32px", md: "0" } }}>
          <img
            src={Logo}
            alt="ConvertDM"
            style={{ height: 32, objectFit: "contain", display: "block" }}
          />
        </Box>

        {/* Centered form block */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 400 }}>
            <Typography
              component="h1"
              sx={{
                fontSize: 24,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
                letterSpacing: "-0.01em",
                m: 0,
              }}
            >
              Welcome to ConvertDM
            </Typography>
            <Typography
              sx={{
                mt: "8px",
                fontSize: 14,
                color: "#64748b",
                lineHeight: 1.5,
              }}
            >
              Convert Instagram comments to DMs at scale.
            </Typography>

            <Box sx={{ mt: "24px" }}>
              {step === "phone" ? (
                <>
                  <Box
                    component="label"
                    sx={{
                      display: "block",
                      fontSize: 13,
                      fontWeight: 500,
                      color: "#334155",
                      mb: "6px",
                    }}
                  >
                    Phone number
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      overflow: "hidden",
                      transition: "border-color 150ms ease-out, box-shadow 150ms ease-out",
                      "&:focus-within": {
                        borderColor: "#f97316",
                        boxShadow: "0 0 0 3px rgba(249,115,22,0.1)",
                      },
                    }}
                  >
                    <Box
                      component="select"
                      value={countryCode}
                      onChange={(e) => setCountryCode(e.target.value)}
                      sx={{
                        border: "none",
                        outline: "none",
                        backgroundColor: "#f8fafc",
                        borderRight: "1px solid #e2e8f0",
                        fontSize: 14,
                        color: "#0f172a",
                        px: "10px",
                        cursor: "pointer",
                        minWidth: 92,
                      }}
                    >
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code + c.country} value={c.code}>
                          {c.flag} {c.code}
                        </option>
                      ))}
                    </Box>
                    <Box
                      component="input"
                      type="tel"
                      placeholder="Enter phone number"
                      value={phoneNumber}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^\d\s]/g, "");
                        setPhoneNumber(val);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSendOTP();
                      }}
                      autoFocus
                      sx={{
                        flex: 1,
                        border: "none",
                        outline: "none",
                        padding: "12px 14px",
                        fontSize: 15,
                        color: "#0f172a",
                        backgroundColor: "#ffffff",
                        "&::placeholder": { color: "#94a3b8" },
                      }}
                    />
                  </Box>

                  <Box sx={{ mt: "16px" }}>
                    <Button
                      id="send-otp-button"
                      variant="primary"
                      size="lg"
                      fullWidth
                      loading={sendingOTP}
                      disabled={!phoneReady}
                      onClick={handleSendOTP}
                    >
                      {sendingOTP ? "Sending OTP..." : "Send OTP"}
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      textAlign: "center",
                      fontSize: 15,
                      fontWeight: 600,
                      color: "#0f172a",
                    }}
                  >
                    {countryCode} {phoneNumber}
                  </Box>
                  <Box sx={{ textAlign: "center", mt: "4px" }}>
                    <Box
                      component="button"
                      onClick={handleChangeNumber}
                      sx={{
                        background: "none",
                        border: "none",
                        color: "#f97316",
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                        padding: 0,
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      Change number
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: { xs: "8px", sm: "10px" },
                      justifyContent: "center",
                      mt: "24px",
                    }}
                  >
                    {otp.map((digit, index) => (
                      <Box
                        key={index}
                        component="input"
                        ref={(el) => (otpInputsRef.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOTPChange(index, e.target.value)}
                        onKeyDown={(e) => handleOTPKeyDown(index, e)}
                        onPaste={index === 0 ? handleOTPPaste : undefined}
                        autoComplete="one-time-code"
                        sx={{
                          width: { xs: 42, sm: 48 },
                          height: { xs: 48, sm: 54 },
                          textAlign: "center",
                          fontSize: 22,
                          fontWeight: 600,
                          color: "#0f172a",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          outline: "none",
                          backgroundColor: "#ffffff",
                          transition: "all 150ms ease-out",
                          "&:focus": {
                            borderColor: "#f97316",
                            boxShadow: "0 0 0 3px rgba(249,115,22,0.1)",
                          },
                        }}
                      />
                    ))}
                  </Box>

                  <Box sx={{ mt: "20px" }}>
                    <Button
                      variant="primary"
                      size="lg"
                      fullWidth
                      loading={verifyingOTP}
                      disabled={!otpReady}
                      onClick={handleVerifyOTP}
                    >
                      {verifyingOTP ? "Verifying..." : "Verify OTP"}
                    </Button>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "6px",
                      mt: "12px",
                      fontSize: 13,
                      color: "#64748b",
                    }}
                  >
                    <span>Didn't receive the code?</span>
                    <Box
                      component="button"
                      onClick={handleResendOTP}
                      disabled={resendTimer > 0}
                      sx={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        fontSize: 13,
                        fontWeight: 500,
                        color: resendTimer > 0 ? "#94a3b8" : "#f97316",
                        cursor: resendTimer > 0 ? "default" : "pointer",
                        "&:hover:not(:disabled)": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {resendTimer > 0
                        ? `Resend in ${resendTimer}s`
                        : "Resend OTP"}
                    </Box>
                  </Box>
                </>
              )}
            </Box>

            <Typography
              sx={{
                mt: "20px",
                fontSize: 12,
                color: "#94a3b8",
                lineHeight: 1.5,
                textAlign: "center",
              }}
            >
              By continuing, you agree to our{" "}
              <Box
                component="a"
                href="https://convertdm.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#f97316",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Terms
              </Box>{" "}
              and{" "}
              <Box
                component="a"
                href="https://convertdm.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#f97316",
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Privacy
              </Box>
              .
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            mt: "24px",
            fontSize: 12,
            color: "#94a3b8",
          }}
        >
          © 2026 ConvertDM
        </Box>
      </Box>

      {/* RIGHT PANEL — gradient w/ quote (desktop only) */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flex: "0 0 50%",
          position: "relative",
          alignItems: "center",
          justifyContent: "center",
          p: "48px",
          background:
            "linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #C2410C 100%)",
          overflow: "hidden",
        }}
      >
        {/* Subtle orange glow */}
        <Box
          sx={{
            position: "absolute",
            bottom: -120,
            right: -120,
            width: 360,
            height: 360,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(249,115,22,0.35) 0%, rgba(249,115,22,0) 70%)",
            pointerEvents: "none",
          }}
        />

        {/* Quote card */}
        <Box
          sx={{
            position: "relative",
            maxWidth: 460,
            backgroundColor: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: "16px",
            p: "32px",
            color: "#f8fafc",
          }}
        >
          <Box sx={{ display: "flex", gap: "4px", mb: "20px" }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} size={16} fill="#f59e0b" color="#f59e0b" />
            ))}
          </Box>
          <Box
            component="blockquote"
            sx={{
              m: 0,
              fontSize: 20,
              fontWeight: 500,
              lineHeight: 1.45,
              color: "#f8fafc",
              letterSpacing: "-0.01em",
            }}
          >
            "ConvertDM helped us turn 10,000 comments into 2,300 qualified DMs
            in 90 days."
          </Box>
          <Box sx={{ mt: "24px", display: "flex", alignItems: "center", gap: "12px" }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: "rgba(249,115,22,0.25)",
                border: "1px solid rgba(249,115,22,0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 700,
                color: "#fdba74",
              }}
            >
              AR
            </Box>
            <Box>
              <Box sx={{ fontSize: 14, fontWeight: 600, color: "#f8fafc" }}>
                Aditi Rao
              </Box>
              <Box sx={{ fontSize: 13, color: "#cbd5e1" }}>
                Founder, Blush Studio
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Invisible reCAPTCHA container */}
      <div id="recaptcha-container"></div>
    </Box>
  );
}

export default LoginPage;
